import React, { useEffect, useMemo } from 'react';
import { cities } from '../data/cities';
import { contentNotes } from '../data/contentNotes';
import { sports } from '../data/sports';
import { useSportInventory } from '../hooks/useSportInventory';
import type { CitySlug, SportSlug } from '../types/landing';
import { sportsOfferedIn } from '../utils/inventory';
import { fillTemplate } from '../utils/template';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ExploreMore } from '../components/ExploreMore';
import { FinalCta } from '../components/FinalCta';
import { FindNearYou } from '../components/FindNearYou/FindNearYou';
import { MobileStickyCta } from '../components/MobileStickyCta';
import { NotesLegend } from '../components/NotesLegend';
import { Reviews } from '../components/Reviews';
import { SeoIntro } from '../components/SeoIntro';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { SportHero } from '../components/SportHero';
import { WhyPlay } from '../components/WhyPlay';
interface CitySportLandingProps {
  sportSlug: SportSlug;
  citySlug: CitySlug;
  onSelectSport: (slug: SportSlug) => void;
  onOpenMetro: () => void;
}
export function CitySportLanding({
  sportSlug,
  citySlug,
  onSelectSport,
  onOpenMetro
}: CitySportLandingProps) {
  const city = cities.find(c => c.slug === citySlug) ?? cities[0];
  const sport = sports.find(s => s.slug === sportSlug) ?? sports[0];
  const inventory = useSportInventory(city, sport);
  const tokens = useMemo(() => ({
    city: city.name,
    sport: sport.noun,
    neighborhoods: city.neighborhoods,
    venueType: sport.venueType
  }), [city, sport]);
  const otherSports = useMemo(() => {
    const offered = sportsOfferedIn(city);
    return sports.filter(s => s.slug !== sport.slug && offered.includes(s.slug));
  }, [city, sport]);
  useEffect(() => {
    document.title = `${sport.leagueTitle} in ${city.name}, ${city.state} | i9 Sports`;
  }, [sport, city]);
  const scrollToFind = () => document.getElementById('find-near-you')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
  return <div className="min-h-screen w-full" data-id="element-256" data-mp="frnj3w1as0rw">
      <SiteHeader data-id="element-257" data-mp="b05k3diu4a9k" />
      <main className="mx-auto w-full max-w-6xl" data-id="element-258" data-mp="azm2xz10dpwl">
        <div className="px-4 md:px-0" data-id="element-259" data-mp="ayawfrg6i41s">
          <Breadcrumbs items={[{
          label: 'Home',
          href: '/'
        }, {
          label: `${city.name} Youth Sports`,
          href: `/${city.slug}`,
          onClick: onOpenMetro
        }, {
          label: sport.name
        }]} data-id="element-260" data-mp="61ubt786u7tg" />
        </div>
        <div className="overflow-hidden bg-white shadow-[0_0_0_1px_#E3E7EE]" data-id="element-261" data-mp="3savjjcmoucro">
          <SportHero title={`${sport.leagueTitle} in ${city.name}, ${city.state}`} intro={fillTemplate(sport.heroIntro, tokens)} image={sport.heroImage} imageAlt={`Kids playing i9 Sports ${sport.noun} in ${city.name}`} ctaLabel={`Find ${sport.name} Programs`} onCta={scrollToFind} data-id="element-262" data-mp="3o77byty23h6f" />
          <SeoIntro heading={fillTemplate(sport.seoHeading, tokens)} paragraphs={sport.seoParagraphs.map(p => fillTemplate(p, tokens))} data-id="element-263" data-mp="1p0s6zoi7ozhq" />
          <WhyPlay sport={sport} tokens={tokens} onCta={scrollToFind} data-id="element-264" data-mp="1yq2119wm8s8h" />
          <FindNearYou sport={sport} city={city} inventory={inventory} data-id="element-265" data-mp="1yrwlkn3z6ic1" />
          <Reviews citySlug={city.slug} sportSlug={sport.slug} data-id="element-266" data-mp="23nlgue8ypd2j" />
          <ExploreMore heading={`Explore More Youth Sports in ${city.name}`} cityName={city.name} citySlug={city.slug} sports={otherSports} onSelectSport={onSelectSport} onOpenMetro={onOpenMetro} data-id="element-267" data-mp="161nzwla8dfsp" />
          <FinalCta title={`Find ${sport.name} Programs Near You`} citySlug={city.slug} sportSlug={sport.slug} browseLabel={`Or browse every ${sport.noun} location on the map`} onBrowse={scrollToFind} data-id="element-268" data-mp="1k0xi1qhzyjzb" />
        </div>
      </main>
      <div className="mt-12" data-id="element-269" data-mp="2tyx97phl1450">
        <SiteFooter data-id="element-270" data-mp="2ty404hv5i2jp" />
      </div>
      <MobileStickyCta label={`Find ${sport.name} Programs`} onClick={scrollToFind} data-id="element-271" data-mp="2tz5m3hdpg9g7" />
      <NotesLegend notes={contentNotes} title="City + Sport template notes" subtitle="Same layout serves /{city}/{sport}" data-id="element-272" data-mp="2p140aoow9w11" />
    </div>;
}